import {useRef, useState} from 'react'
import {Button} from '@primer/react'
import type {PullRequestUserActionStack} from '@github-ui/pull-requests/analytics/pull-request-user-action'

import {RebaseStackConfirmationDialog} from '../../../common/RebaseStackConfirmationDialog'
import {ResolveConflictsDialog} from '../../../common/ResolveConflictsDialog'
import {useRebaseStack} from '../../../../../hooks/use-rebase-stack'
import type {StackSectionStackEntry} from '../../../../../helpers/merge-box-status-calculator/stack-section-status'

/**
 * CTA that opens the stack rebase confirmation dialog. Confirming delegates to
 * `onConfirmRebase`, which starts the rebase mutation (owned by MergeBox so the
 * mutation observer outlives this button). It reads the in-progress state from
 * the shared `useRebaseStack` hook for display only.
 *
 * Renders nothing when the viewer can't rebase (no push access, blocked by an authorization
 * policy, or the stack is collapsed to just its list). When the base branch requires signed
 * commits, the rebase must happen on the command line, so it opens the shared CLI
 * instructions dialog instead of the in-app confirmation dialog.
 */
export function RebaseStackButton({
  stackEntries,
  stackMetadata,
  canRebase,
  stackBaseRefRequiresSignatures,
  onConfirmRebase,
}: {
  stackEntries: StackSectionStackEntry[]
  stackMetadata?: PullRequestUserActionStack
  canRebase: boolean
  stackBaseRefRequiresSignatures: boolean
  onConfirmRebase: () => void
}) {
  const [showDialog, setShowDialog] = useState(false)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const {isRebasing} = useRebaseStack({stackEntries, stackMetadata})

  if (!canRebase) return null

  const closeDialog = () => {
    setShowDialog(false)
    buttonRef.current?.focus()
  }

  return (
    <>
      <Button
        ref={buttonRef}
        onClick={() => setShowDialog(true)}
        loading={isRebasing}
        loadingAnnouncement="Rebasing stack"
      >
        Rebase stack
      </Button>
      {showDialog && stackBaseRefRequiresSignatures && (
        <ResolveConflictsDialog
          stackNumber={stackMetadata?.number ?? 0}
          returnFocusRef={buttonRef}
          onClose={closeDialog}
        />
      )}
      {showDialog && !stackBaseRefRequiresSignatures && (
        <RebaseStackConfirmationDialog onConfirm={onConfirmRebase} onClose={closeDialog} />
      )}
    </>
  )
}
